import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Parsed address fields from the geocoding result
 */
export interface ParsedAddress {
  unitNumber: string;
  street: string;
  city: string;
  suburb: string;
  province: string;
  postalCode: string;
  country: string;
  fullAddress: string;
  lat?: number;
  lon?: number;
}

interface AddressSuggestion {
  place_id: number;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    neighbourhood?: string;
    suburb?: string;
    city?: string;
    town?: string;
    village?: string;
    hamlet?: string;
    municipality?: string;
    county?: string;
    state?: string;
    state_district?: string;
    province?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
  lat: string;
  lon: string;
}

interface AddressAutocompleteProps {
  onAddressSelect: (address: ParsedAddress) => void;
  defaultValue?: string;
  placeholder?: string;
  label?: string;
  countryCode?: string;
  size?: "small" | "medium";
  error?: boolean;
  helperText?: string;
}

/**
 * Address autocomplete using free Nominatim (OpenStreetMap) geocoding API.
 *
 * IMPORTANT: Nominatim usage policy requires identification.
 * We pass `email` param (not User-Agent, which browsers block).
 * See: https://operations.osmfoundation.org/policies/nominatim/
 */
const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  onAddressSelect,
  defaultValue = "",
  placeholder = "Start typing an address...",
  label,
  countryCode = "ca",
  size = "small",
  error,
  helperText,
}) => {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (query.length < 3) {
        setOptions([]);
        setHasSearched(false);
        return;
      }

      // Cancel any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      setLoading(true);
      setHasSearched(true);

      try {
        // Build query params — include email for Nominatim identification
        const params = new URLSearchParams({
          q: query,
          format: "jsonv2",
          addressdetails: "1",
          limit: "8",
          dedupe: "1",
          // Nominatim usage policy asks callers to include a contact email.
          // Use the configured company email when available so a fresh tenant
          // sends its own address; fall back to a neutral placeholder rather
          // than any tenant's brand.
          email: (typeof window !== "undefined" && (window as any).__COMPANY_EMAIL__) || "admin@example.com",
        });

        if (countryCode) {
          params.set("countrycodes", countryCode);
        }

        const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;

        const response = await fetch(url, {
          signal: abortRef.current.signal,
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data: AddressSuggestion[] = await response.json();
        setOptions(data || []);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          console.error("Address lookup failed:", err);
          setOptions([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [countryCode]
  );

  // Debounced search — 1000ms to respect Nominatim's 1 req/sec limit
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      if (inputValue.length >= 3) {
        fetchSuggestions(inputValue);
      }
    }, 1000);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, fetchSuggestions]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const parseAddress = (suggestion: AddressSuggestion): ParsedAddress => {
    const addr = suggestion.address || {};
    const houseNumber = addr.house_number || "";
    const road = addr.road || "";
    const street = [houseNumber, road].filter(Boolean).join(" ");

    const city =
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      addr.municipality ||
      addr.county ||
      "";

    const suburb = addr.suburb || addr.neighbourhood || "";

    const province =
      addr.state || addr.province || addr.state_district || "";

    const postalCode = addr.postcode || "";
    const country = addr.country || "";

    return {
      unitNumber: "",
      street,
      city,
      suburb,
      province,
      postalCode,
      country,
      fullAddress: suggestion.display_name,
      lat: parseFloat(suggestion.lat),
      lon: parseFloat(suggestion.lon),
    };
  };

  const handleSelect = (_: React.SyntheticEvent, value: any) => {
    if (value && typeof value !== "string") {
      const parsed = parseAddress(value as AddressSuggestion);
      setInputValue(
        [parsed.street, parsed.city, parsed.province, parsed.postalCode]
          .filter(Boolean)
          .join(", ")
      );
      onAddressSelect(parsed);
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={options}
      loading={loading}
      getOptionLabel={(option: any) => {
        if (typeof option === "string") return option;
        return option.display_name || "";
      }}
      isOptionEqualToValue={(option: any, value: any) =>
        option.place_id === value.place_id
      }
      filterOptions={(x) => x}
      inputValue={inputValue}
      onInputChange={(_, val, reason) => {
        if (reason === "input") {
          setInputValue(val);
        }
        if (reason === "clear") {
          setInputValue("");
          setOptions([]);
          setHasSearched(false);
        }
      }}
      onChange={handleSelect}
      renderOption={(props, option: any) => {
        const addr = option.address || {};
        const street = [addr.house_number, addr.road].filter(Boolean).join(" ");
        const area = [
          addr.suburb || addr.neighbourhood,
          addr.city || addr.town || addr.village,
        ]
          .filter(Boolean)
          .join(", ");
        const region = [
          addr.state || addr.province,
          addr.postcode,
          addr.country,
        ]
          .filter(Boolean)
          .join(", ");

        return (
          <li {...props} key={option.place_id || `${option.lat}-${option.lon}`}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, py: 0.5 }}>
              <LocationOnIcon
                sx={{ fontSize: 20, color: "rgba(0,0,0,0.3)", mt: 0.25, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>
                  {street || option.display_name.split(",")[0]}
                </Typography>
                {area && (
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary", lineHeight: 1.3 }}>
                    {area}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 10.5, color: "text.disabled", lineHeight: 1.3 }}>
                  {region}
                </Typography>
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          fullWidth
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.3)" }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress size={18} sx={{ mr: 1 }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={
        !hasSearched
          ? "Type at least 3 characters to search..."
          : loading
          ? "Searching..."
          : "No addresses found. Try a different search."
      }
      loadingText="Searching addresses..."
    />
  );
};

export default AddressAutocomplete;
